/*
 * Shared promo countdown — the SINGLE source of truth for every countdown that
 * shows the promotion's remaining time.
 *
 * The deadline is EVERGREEN AND DAILY: 23:59:59 of the current day in
 * Europe/Rome, wherever the visitor actually is. There is nothing to persist
 * and nothing to roll over by hand — every tick recomputes the time left from
 * Rome's own wall clock, so the value is identical on every page, it restarts
 * on its own after midnight, and DST is handled by the platform's timezone
 * database rather than by arithmetic here.
 *
 * Consumers: the product page's offer pill (snippets/product-bundle-offer),
 * the <countdown-timer> element below (sections/collection-banner) and the
 * legacy tier snippet. They all read this one module, so they can never drift.
 */
(function () {
  var LAST_SECOND_OF_DAY = 86399; // 23:59:59, in seconds since midnight

  /* Rome's wall clock, formatted by the platform (DST included). Built once:
     Intl.DateTimeFormat is expensive to construct, cheap to reuse. */
  var romeClock = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Rome',
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });

  function secondsIntoRomeDay(now) {
    var h = 0;
    var m = 0;
    var s = 0;
    romeClock.formatToParts(now).forEach(function (part) {
      // Some engines render midnight as "24" under hour12:false — fold it to 0.
      if (part.type === 'hour') h = parseInt(part.value, 10) % 24;
      else if (part.type === 'minute') m = parseInt(part.value, 10);
      else if (part.type === 'second') s = parseInt(part.value, 10);
    });
    return h * 3600 + m * 60 + s;
  }

  /* Milliseconds until 23:59:59 tonight, Rome time. Never negative: during
     that final second it reads 0, and the next tick after midnight is back to
     a full 23:59:59. */
  function remaining() {
    var now = new Date();
    var left = (LAST_SECOND_OF_DAY - secondsIntoRomeDay(now)) * 1000 - now.getMilliseconds();
    return left > 0 ? left : 0;
  }

  function endsAt() {
    return Date.now() + remaining();
  }

  function pad(n) {
    return String(n).length < 2 ? '0' + String(n) : String(n);
  }

  function parts(ms) {
    return {
      hours: Math.floor(ms / 3600000),
      minutes: Math.floor((ms % 3600000) / 60000),
      seconds: Math.floor((ms % 60000) / 1000)
    };
  }

  /* ONE interval for every display. Each subscriber is handed the same
     `remaining()` value from the same tick, so two timers on the same page can
     never render a second apart. */
  var subscribers = [];
  var interval = null;

  function broadcast() {
    var ms = remaining();
    for (var i = 0; i < subscribers.length; i++) {
      try { subscribers[i](ms); } catch (e) { /* one bad display can't stop the rest */ }
    }
  }

  function subscribe(fn) {
    if (typeof fn !== 'function') return function () {};
    subscribers.push(fn);
    fn(remaining());
    if (!interval) interval = setInterval(broadcast, 1000);
    return function unsubscribe() {
      var i = subscribers.indexOf(fn);
      if (i !== -1) subscribers.splice(i, 1);
      if (!subscribers.length && interval) {
        clearInterval(interval);
        interval = null;
      }
    };
  }

  window.Velare = window.Velare || {};
  window.Velare.promoCountdown = {
    endsAt: endsAt,
    remaining: remaining,
    subscribe: subscribe,
    parts: parts,
    pad: pad
  };
})();

if (!customElements.get('countdown-timer')) {
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.output = this.querySelector('[data-countdown-output]');
      // Segmented variant (announcement bar): separate DAY / HR / MIN / SEC
      // cells. Day and hour are optional — without them the minute cell keeps
      // showing the total minutes, as it did when the bar had two segments.
      this.dayEl = this.querySelector('[data-countdown-day]');
      this.hourEl = this.querySelector('[data-countdown-hour]');
      this.minEl = this.querySelector('[data-countdown-min]');
      this.secEl = this.querySelector('[data-countdown-sec]');

      if (!this.output && !(this.minEl && this.secEl)) {
        this.hidden = true;
        return;
      }

      /* Painted by the shared tick, so this bar and the product page's offer
         badge update on the very same beat from the very same value. */
      const shared = window.Velare.promoCountdown;
      const pad = shared.pad;
      this.unsubscribe = shared.subscribe((ms) => {
        const { hours, minutes, seconds } = shared.parts(ms);
        if (this.minEl && this.secEl) {
          if (this.dayEl || this.hourEl) {
            // Four segments: the SAME remaining time, only split further —
            // `hours` is the total hours left, so days = hours / 24.
            if (this.dayEl) this.dayEl.textContent = pad(Math.floor(hours / 24));
            if (this.hourEl) this.hourEl.textContent = pad(hours % 24);
            this.minEl.textContent = pad(minutes);
          } else {
            // Two segments only: hours (if a merchant sets >60') fold into minutes.
            this.minEl.textContent = pad(hours * 60 + minutes);
          }
          this.secEl.textContent = pad(seconds);
        }
        if (this.output) {
          // Tight HH:MM:SS, always three segments (00:44:59 style) — the
          // 45-minute window would otherwise render as bare MM:SS. Only the
          // hero pill consumes this output.
          this.output.textContent = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
        }
      });
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }
  }

  customElements.define('countdown-timer', CountdownTimer);
}
